/**
 * camera feature – all pure utilities (camera, layout, preview, image editor).
 * No React; safe to test and reuse.
 */

import { SHUTTER_SOUND_SRC, DEFAULT_ZOOM, JPEG_QUALITY } from "./constants";

/**
 * Phones often open the back camera with deviceId but the front shows a black preview (constraint / driver quirk).
 * Switching with facingMode user/environment matches the OS camera behavior and avoids that failure mode.
 */
export function preferFacingModeForCameraPreview() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
  return false;
}

// ----- Camera (base64, file conversion) -----

/**
 * @param {string[]} base64Array
 * @returns {File[]}
 */
export function base64ArrayToFileArray(base64Array) {
  if (!Array.isArray(base64Array)) return [];
  try {
    return base64Array.map((base64, index) => {
      if (typeof base64 !== "string" || !base64.includes(",")) throw new Error("Invalid base64");
      const [meta, content] = base64.split(",");
      const mimeMatch = meta.match(/data:(.*);base64/);
      if (!mimeMatch) throw new Error("Invalid base64 format");
      const mime = mimeMatch[1];
      const byteString = atob(content);
      const arr = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) arr[i] = byteString.charCodeAt(i);
      const ext = mime.split("/")[1] || "file";
      return new File([new Blob([arr], { type: mime })], `file_${index}.${ext}`, { type: mime });
    });
  } catch {
    return [];
  }
}

// ----- Layout / pipeline -----

export function formatRecordingTime(ms) {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export function getImageSrcFromItem(item) {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") return item.image ?? item.src ?? item.url ?? item;
  return item;
}

export function isPdfItem(item) {
  return (
    typeof item === "string" &&
    (item.toLowerCase().includes(".pdf") || item.startsWith("data:application/pdf"))
  );
}

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function fileHasAllowedExtension(file, allowedExtensions) {
  const name = (file.name || "").toLowerCase();
  return allowedExtensions.some((ext) => name.endsWith(ext.toLowerCase()));
}

export function getDefaultCameraFullScreen() {
  if (typeof window === "undefined") return false;
  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const narrowScreen = window.innerWidth < 768;
  return !coarsePointer && !narrowScreen;
}

// ----- OCR center crop -----

/**
 * Crops an image file to the center region. Use when guiding the user with a center box overlay
 * so the backend receives only the region of interest (e.g. odometer) and less noise.
 *
 * @param {File} file - Image file (e.g. image/png, image/jpeg)
 * @param {number} [widthRatio=0.7] - Width of center region as fraction of image width (0–1)
 * @param {number} [heightRatio=0.5] - Height of center region as fraction of image height (0–1)
 * @returns {Promise<File>} Cropped image as a new File (JPEG fallback if original type cannot be encoded)
 * @rejects {Error} When the image cannot be decoded, drawn, or exported (never returns uncropped file)
 */
export function cropImageToCenterRegion(file, widthRatio = 0.7, heightRatio = 0.5) {
  const wR = Math.max(0.01, Math.min(1, Number(widthRatio) || 0.7));
  const hR = Math.max(0.01, Math.min(1, Number(heightRatio) || 0.5));

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        URL.revokeObjectURL(url);
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) {
          reject(new Error("Invalid image dimensions"));
          return;
        }
        const cw = Math.round(w * wR);
        const ch = Math.round(h * hR);
        const sx = Math.round((w - cw) / 2);
        const sy = Math.round((h - ch) / 2);
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not process image"));
          return;
        }
        ctx.drawImage(img, sx, sy, cw, ch, 0, 0, cw, ch);
        const preferredMime = (file.type || "image/jpeg").split(";")[0];
        const base = (file.name || "cropped").replace(/\.[^.]+$/, "");

        const tryExport = (exportMime, quality) => {
          canvas.toBlob(
            (blob) => {
              if (!blob && exportMime !== "image/jpeg") {
                tryExport("image/jpeg", 0.92);
                return;
              }
              if (!blob) {
                reject(new Error("Could not export cropped image"));
                return;
              }
              const ext = exportMime === "image/jpeg" ? "jpg" : exportMime.split("/")[1] || "jpg";
              resolve(new File([blob], `${base}_cropped.${ext}`, { type: exportMime }));
            },
            exportMime,
            quality
          );
        };

        tryExport(preferredMime, 0.92);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Crop failed"));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image"));
    };
    img.src = url;
  });
}

/**
 * Lightweight validation of an image for OCR: detects blank/uniform or too dark/bright images
 * so the user can be asked to align the reading in the center frame.
 *
 * @param {File} file - Image file to validate
 * @returns {Promise<{ valid: boolean, reason?: 'no_content' | 'poor_quality' }>}
 */
export function validateOcrImage(file) {
  if (!file?.type?.startsWith("image/")) {
    return Promise.resolve({ valid: true });
  }

  const SAMPLE_SIZE = 64;
  const MIN_VARIANCE = 80;
  const MIN_AVG = 25;
  const MAX_AVG = 230;

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        URL.revokeObjectURL(url);
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) {
          resolve({ valid: false, reason: "no_content" });
          return;
        }
        const scale = Math.min(1, SAMPLE_SIZE / w, SAMPLE_SIZE / h);
        const cw = Math.max(1, Math.round(w * scale));
        const ch = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve({ valid: true });
          return;
        }
        ctx.drawImage(img, 0, 0, w, h, 0, 0, cw, ch);
        const data = ctx.getImageData(0, 0, cw, ch).data;
        let sum = 0;
        let sumSq = 0;
        const n = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          const L = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          sum += L;
          sumSq += L * L;
        }
        const avg = sum / n;
        const variance = sumSq / n - (sum / n) * (sum / n);
        if (variance < MIN_VARIANCE) {
          resolve({ valid: false, reason: "no_content" });
          return;
        }
        if (avg < MIN_AVG || avg > MAX_AVG) {
          resolve({ valid: false, reason: "poor_quality" });
          return;
        }
        resolve({ valid: true });
      } catch {
        resolve({ valid: true });
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: true });
    };
    img.src = url;
  });
}

// ----- Camera preview -----

/**
 * @param {MediaTrackCapabilities} [cap]
 * @returns {{ min: number, max: number, step: number } | null}
 */
export function getZoomRange(cap) {
  if (!cap || cap.zoom == null) return null;
  const z = cap.zoom;
  if (typeof z === "object" && typeof z.min === "number" && typeof z.max === "number") {
    return {
      min: z.min,
      max: z.max,
      step: typeof z.step === "number" ? z.step : DEFAULT_ZOOM.step,
    };
  }
  if (typeof z === "number" && z > 1) {
    return { min: 1, max: z, step: DEFAULT_ZOOM.step };
  }
  return { ...DEFAULT_ZOOM };
}

export function playShutterSound() {
  try {
    const audio = new Audio(SHUTTER_SOUND_SRC);
    audio.volume = 0.5;
    audio.play().catch((err) => {
      if (process.env.NODE_ENV === "development") {
        console.warn("Shutter sound failed:", err);
      }
    });
  } catch {
    // No Audio support (e.g. some test envs)
  }
}

/**
 * @param {number} vw - video width
 * @param {number} vh - video height
 * @param {number} cw - container width
 * @param {number} ch - container height
 * @returns {{ sx: number, sy: number, sw: number, sh: number } | null}
 */
export function getCoverSourceRect(vw, vh, cw, ch) {
  if (vw <= 0 || vh <= 0 || cw <= 0 || ch <= 0) return null;
  const scale = Math.max(cw / vw, ch / vh);
  if (scale === cw / vw) {
    const sh = Math.round((ch * vw) / cw);
    const sy = Math.round((vh - sh) / 2);
    return { sx: 0, sy, sw: vw, sh };
  }
  const sw = Math.round((cw * vh) / ch);
  const sx = Math.round((vw - sw) / 2);
  return { sx, sy: 0, sw, sh: vh };
}

// ----- OCR real-time alignment (angle from video frame) -----

const ALIGN_SAMPLE_SIZE = 128;
const ANGLE_BINS = 36;
const DEG_PER_BIN = 180 / ANGLE_BINS;
const MIN_EDGE_STRENGTH = 18;

function luminanceAt(data, i) {
  return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
}

/**
 * 3x3 Sobel gradient at pixel (x,y). Returns { gx, gy, mag }.
 */
function sobelAt(data, cw, ch, x, y) {
  const stride = cw * 4;
  const i = (y * cw + x) * 4;
  const L = (dx, dy) => luminanceAt(data, i + dx * 4 + dy * stride);
  const gx = -L(-1, -1) + L(1, -1) - 2 * L(-1, 0) + 2 * L(1, 0) - L(-1, 1) + L(1, 1);
  const gy = -L(-1, -1) - 2 * L(0, -1) - L(1, -1) + L(-1, 1) + 2 * L(0, 1) + L(1, 1);
  const mag = Math.hypot(gx, gy);
  return { gx, gy, mag };
}

/**
 * Parabolic interpolation for sub-bin angle: given histogram and max bin index,
 * returns refined angle in [0, 180).
 */
function refineAngleFromHistogram(hist, maxBin) {
  const n = hist.length;
  const prev = hist[(maxBin - 1 + n) % n];
  const curr = hist[maxBin];
  const next = hist[(maxBin + 1) % n];
  const denom = prev - 2 * curr + next;
  if (Math.abs(denom) < 1e-6) return (maxBin + 0.5) * DEG_PER_BIN;
  const offset = (0.5 * (prev - next)) / denom;
  const bin = maxBin + offset;
  const b = ((bin % n) + n) % n;
  let angle = b * DEG_PER_BIN;
  if (angle >= 180) angle -= 180;
  return angle;
}

/**
 * Estimates the dominant edge angle in the center region of a video frame (for OCR alignment).
 * Uses Sobel gradients and parabolic sub-bin refinement for higher accuracy.
 * Returns angle in degrees [0, 180). Text aligned at 0° or 90° is considered straight.
 *
 * @param {HTMLVideoElement} videoEl
 * @param {number} [widthRatio=0.7] - center region width as fraction of video width
 * @param {number} [heightRatio=0.5] - center region height as fraction of video height
 * @returns {{ angle: number } | null} Dominant angle in degrees, or null if cannot compute
 */
export function estimateDominantAngleFromVideo(videoEl, widthRatio = 0.7, heightRatio = 0.5) {
  if (!videoEl || videoEl.readyState < 2) return null;
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  if (!vw || !vh) return null;
  const sw = Math.max(1, Math.round(vw * widthRatio));
  const sh = Math.max(1, Math.round(vh * heightRatio));
  const sx = Math.round((vw - sw) / 2);
  const sy = Math.round((vh - sh) / 2);
  const scale = Math.min(1, ALIGN_SAMPLE_SIZE / sw, ALIGN_SAMPLE_SIZE / sh);
  const cw = Math.max(3, Math.round(sw * scale));
  const ch = Math.max(3, Math.round(sh * scale));
  try {
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(videoEl, sx, sy, sw, sh, 0, 0, cw, ch);
    const data = ctx.getImageData(0, 0, cw, ch).data;
    const hist = new Array(ANGLE_BINS).fill(0);
    for (let y = 1; y < ch - 1; y++) {
      for (let x = 1; x < cw - 1; x++) {
        const { gx, gy, mag } = sobelAt(data, cw, ch, x, y);
        if (mag < MIN_EDGE_STRENGTH) continue;
        let deg = (Math.atan2(gy, gx) * 180) / Math.PI;
        if (deg < 0) deg += 180;
        const bin = Math.min(ANGLE_BINS - 1, Math.floor(deg / DEG_PER_BIN));
        hist[bin] += mag * mag;
      }
    }
    let maxSum = 0;
    let maxBin = 0;
    for (let b = 0; b < ANGLE_BINS; b++) {
      if (hist[b] > maxSum) {
        maxSum = hist[b];
        maxBin = b;
      }
    }
    if (maxSum < 1) return null;
    const angle = refineAngleFromHistogram(hist, maxBin);
    return { angle };
  } catch {
    return null;
  }
}

/**
 * Returns true if angle (degrees 0–180) is close to 0° or 90° (text straight).
 * @param {number} angle - dominant angle from estimateDominantAngleFromVideo
 * @param {number} [toleranceDeg=14] - allowed deviation from 0 or 90
 */
export function isAngleAlignedForOcr(angle, toleranceDeg = 14) {
  const t = toleranceDeg;
  const near0 = angle <= t || angle >= 180 - t;
  const near90 = angle >= 90 - t && angle <= 90 + t;
  return near0 || near90;
}

/**
 * @param {HTMLVideoElement} videoEl
 * @param {{ width: number, height: number }} containerRect
 * @param {string} [mimeType]
 * @returns {string | null}
 */
export function captureVideoRegionToDataUrl(videoEl, containerRect, mimeType = "image/png") {
  if (!videoEl || videoEl.readyState < 2) return null;
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  const rect = getCoverSourceRect(vw, vh, containerRect.width, containerRect.height);
  if (!rect) return null;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = rect.sw;
    canvas.height = rect.sh;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(videoEl, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.sw, rect.sh);
    return canvas.toDataURL(mimeType);
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Capture region failed:", err);
    }
    return null;
  }
}

/**
 * @param {TouchList} touches
 * @returns {number}
 */
export function getTouchDistance(touches) {
  if (touches.length < 2) return 0;
  const [a, b] = touches;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

// ----- Image editor -----

export async function checkIfPdf(url) {
  if (!url) return false;
  if (url.startsWith("data:application/pdf;base64,")) return true;
  if (url.toLowerCase().includes(".pdf")) return true;
  if (url.startsWith("blob:")) {
    try {
      const response = await fetch(url, { method: "HEAD" });
      const contentType = response.headers.get("content-type");
      return Boolean(contentType?.includes("pdf"));
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * @param {string} dataUrl
 * @param {number} degrees
 * @returns {Promise<string>}
 */
export function rotateImageDataUrl(dataUrl, degrees) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      if (degrees === 90 || degrees === 270) {
        canvas.width = img.height;
        canvas.height = img.width;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas 2d context unavailable"));
        return;
      }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2, img.width, img.height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      try {
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * @param {string} originalImage
 * @param {number} rotationDegrees
 * @param {{ x: number, y: number, width: number, height: number }} completedCrop
 * @param {number} rotatedNaturalWidth
 * @param {number} rotatedNaturalHeight
 * @returns {Promise<string>}
 */
export function getCroppedImageFromOriginal(
  originalImage,
  rotationDegrees,
  completedCrop,
  rotatedNaturalWidth,
  rotatedNaturalHeight
) {
  if (!completedCrop?.width || !completedCrop?.height) {
    return Promise.reject(new Error("Invalid crop"));
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      let cw, ch;
      if (rotationDegrees === 90 || rotationDegrees === 270) {
        cw = h;
        ch = w;
      } else {
        cw = w;
        ch = h;
      }
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas 2d context unavailable"));
        return;
      }
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate((rotationDegrees * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      const sx = (completedCrop.x / 100) * cw;
      const sy = (completedCrop.y / 100) * ch;
      const sw = (completedCrop.width / 100) * cw;
      const sh = (completedCrop.height / 100) * ch;
      const out = document.createElement("canvas");
      out.width = Math.round(sw);
      out.height = Math.round(sh);
      const outCtx = out.getContext("2d");
      if (!outCtx) {
        reject(new Error("Canvas 2d context unavailable"));
        return;
      }
      outCtx.drawImage(canvas, sx, sy, sw, sh, 0, 0, out.width, out.height);
      resolve(out.toDataURL("image/jpeg", JPEG_QUALITY));
    };
    img.onerror = reject;
    img.src = originalImage;
  });
}
